import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";

export function trackEvent(eventType: string, page?: string, metadata?: Record<string, any>) {
  fetch("/api/activity", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      eventType,
      page: page ?? null,
      metadata: metadata ? JSON.stringify(metadata) : null,
    }),
    credentials: "same-origin",
  }).catch(() => {});
}

export function useActivityTracker() {
  const [location] = useLocation();
  const lastLocation = useRef<string | null>(null);
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });

  // Ghost Mode impersonation sessions must leave zero activity traces —
  // the server also drops these events, but skip the request entirely here.
  const isGhost = !!user?.ghostMode;
  const isTrackedRole = !!user && ["subscriber", "owner"].includes(user.role) && !isGhost;

  useEffect(() => {
    if (!isTrackedRole) return;
    if (location === lastLocation.current) return;
    lastLocation.current = location;
    trackEvent("page_view", location);
  }, [location, isTrackedRole]);

  const trackTabChange = useCallback((tabName: string) => {
    if (!isTrackedRole) return;
    trackEvent("tab_change", location, { tab: tabName });
  }, [isTrackedRole, location]);

  const trackFeatureAction = useCallback((action: string, metadata?: Record<string, any>) => {
    if (!isTrackedRole) return;
    trackEvent(action, location, metadata);
  }, [isTrackedRole, location]);

  return { trackEvent, trackTabChange, trackFeatureAction };
}
